import React from 'react';

import { GiroPayment, GiroPaymentsTableSortableField } from 'types/state';
import { useIntlDictionary } from 'hooks/useIntlDictionary';
import {
  TableTr,
  TableWrapper,
  TableHead,
  TableTh,
  TableBody,
} from 'design/table';
import { GiroPaymentsTableRow } from 'containers/GiroPaymentsTable/GiroPaymentsTableRow';

type Props = {
  giroPayments: GiroPayment[];
  sortedBy: GiroPaymentsTableSortableField;
  ascendingSort: boolean;
  activeItemId?: number;
  onSortChange(nextSortBy: GiroPaymentsTableSortableField): void;
  onItemClick(item: number): void;
};

export const GiroPaymentsTable = (props: Props) => {
  const {
    giroPayments,
    sortedBy,
    ascendingSort,
    activeItemId,
    onSortChange,
    onItemClick,
  } = props;

  const { giroPaymentsTableColumns: columnsIntl } = useIntlDictionary();

  const onIdClick = React.useCallback(() => {
    onSortChange('id');
  }, [onSortChange]);

  const onOrganizationClick = React.useCallback(() => {
    onSortChange('organization');
  }, [onSortChange]);

  const onRecievedAtClick = React.useCallback(() => {
    onSortChange('recievedAt');
  }, [onSortChange]);

  const onFullNameClick = React.useCallback(() => {
    onSortChange('fullName');
  }, [onSortChange]);

  const onRecievedAmountClick = React.useCallback(() => {
    onSortChange('recievedAmount');
  }, [onSortChange]);

  const onCreditedAmountClick = React.useCallback(() => {
    onSortChange('creditedAmount');
  }, [onSortChange]);

  const onStatusClick = React.useCallback(() => {
    onSortChange('status');
  }, [onSortChange]);

  const onUpdatedAtClick = React.useCallback(() => {
    onSortChange('updatedAt');
  }, [onSortChange]);

  const tableRowsNode = React.useMemo(() => {
    return giroPayments.map((giroPayment) => (
      <GiroPaymentsTableRow
        key={giroPayment.id}
        active={activeItemId === giroPayment.id}
        giroPayment={giroPayment}
        onClick={onItemClick}
      />
    ));
  }, [giroPayments, activeItemId, onItemClick]);

  return (
    <TableWrapper>
      <TableHead>
        <TableTr>
          <TableTh
            size="xs"
            sorted={sortedBy === 'id'}
            ascending={ascendingSort}
            text={columnsIntl.id}
            onClick={onIdClick}
          />
          <TableTh size="m" text={columnsIntl.unp} />
          <TableTh
            size="auto"
            sorted={sortedBy === 'organization'}
            ascending={ascendingSort}
            text={columnsIntl.organization}
            onClick={onOrganizationClick}
          />
          <TableTh
            size="xxl"
            sorted={sortedBy === 'fullName'}
            ascending={ascendingSort}
            text={columnsIntl.fullName}
            onClick={onFullNameClick}
          />
          <TableTh
            size="l"
            sorted={sortedBy === 'recievedAt'}
            ascending={ascendingSort}
            text={columnsIntl.recievedAt}
            onClick={onRecievedAtClick}
          />
          <TableTh
            size="m"
            sorted={sortedBy === 'recievedAmount'}
            ascending={ascendingSort}
            text={columnsIntl.recievedAmount}
            onClick={onRecievedAmountClick}
          />
          <TableTh
            size="m"
            sorted={sortedBy === 'creditedAmount'}
            ascending={ascendingSort}
            text={columnsIntl.creditedAmount}
            onClick={onCreditedAmountClick}
          />
          <TableTh
            size="l"
            sorted={sortedBy === 'status'}
            ascending={ascendingSort}
            text={columnsIntl.status}
            onClick={onStatusClick}
          />
          <TableTh
            size="m"
            sorted={sortedBy === 'updatedAt'}
            ascending={ascendingSort}
            text={columnsIntl.updatedAt}
            onClick={onUpdatedAtClick}
          />
        </TableTr>
      </TableHead>
      <TableBody>{tableRowsNode}</TableBody>
    </TableWrapper>
  );
};
